import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { NgComponentOutlet, NgStyle } from '@angular/common';
import { injectDialogData, injectDialogRef } from '@radix-ng/primitives/dialog';
import { DialogAction, DialogActionTypeEnum, DialogEventEmitted, DialogParams } from '../../models/dialog.model';
import { IconComponent } from '../../../icon/icon.component';
import { ButtonDirective } from '../../../button/button.directive';
import { Subject } from 'rxjs';
import { SwitchComponent } from '../../../switch/switch.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'ds-dialog',
  host: {
    '[class]': 'classes',
    '[style.width]': 'styles.width',
    '[style.height]': 'styles.height',
    '[style.min-width]': 'styles.minWidth',
    '[style.max-width]': 'styles.maxWidth',
    '[style.min-height]': 'styles.minHeight',
    '[style.max-height]': 'styles.maxHeight',
    '[style.margin]': 'styles.margin',
  },
  imports: [NgComponentOutlet,
            IconComponent,
            ButtonDirective,
            NgStyle,
            SwitchComponent,
            FormsModule,
            ReactiveFormsModule],
  templateUrl: './dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogComponent implements OnDestroy, OnInit {
  /**
   * ================================================
   * Dependencies
   * ================================================
   */
  readonly data = injectDialogData<DialogParams>();
  readonly ref = injectDialogRef();
  /**
   * ================================================
   * Dynamic Component Setup
   * ================================================
   */
  readonly dynamicComponent = this.data.component;
  readonly actions: DialogAction[] = (this.data.actions ?? []).map((actionInput) => {
    const mappedAction: DialogAction = {
      ...actionInput,
      variant: actionInput.variant ?? (actionInput.type === DialogActionTypeEnum.PRIMARY ? 'primary' : 'tertiary-subtle'),
      /**
       * if action binding is enabled, the primary action is handled by the dialog component.
       * so by default it will start disabled.
       */
      $disabled: this.data.withActionBindings
        ? signal(actionInput.disabled ?? actionInput.type === DialogActionTypeEnum.PRIMARY)
        : undefined,
      /**
       * if controlled actions are enabled, rendered component can trigger the action by calling the trigger method.
       * this is useful for components that need to control the actions of the dialog, such as a form.
       */
      trigger: () => this.onActionClick(mappedAction),
    };
    return mappedAction;
  });
  readonly events$ = new Subject<DialogEventEmitted>();
  // Subject to notify changes in compactView
  readonly compactViewChange$ = new Subject<boolean>();
  readonly componentInputs = {
    ...(this.data.withDialogRef ? { dialogRef: this.ref } : {}),
    /**
     * Only add the actions if the withActionBindings | withControlledActions flag is true.
     * This is to avoid passing the actions to the dynamic component if the actions are not needed.
     */
    ...(this.data.withActionBindings || this.data.withControlledActions ? { actions: this.actions } : {}),
    /**
     * Only add the events$ if the withEventsBindings flag is true.
     */
    ...(this.data.withEventsBindings ? { events$: this.events$ } : {}),
    /**
     * Only add the dataChange if the withDataBindings flag is true.
     */
    ...(this.data.withDataBindings ? { dataChange: this.onDataChange.bind(this) } : {}),
    /**
     * Add the component inputs.
     */
    ...this.data.componentInputs,
    /**
     * Add compactViewChange$ for communication
     */
    ...(this.data.withCompactViewChange ? { compactViewChange$: this.compactViewChange$ } : {}),
  };
  readonly classes = [
    'ds-dialog',
    this.data.withHeaderBorder ? 'ds-dialog-with-header-border' : '',
  ].join(' ');
  /**
   * ================================================
   * Styles
   * ================================================
   */
  readonly styles = {
    minWidth: typeof this.data.minWidth === 'number' ? `${this.data.minWidth}px` : this.data.minWidth ?? '400px',
    width: typeof this.data.width === 'number' ? `${this.data.width}px` : this.data.width ?? 'auto',
    height: typeof this.data.height === 'number' ? `${this.data.height}px` : this.data.height ?? 'auto',
    maxWidth: typeof this.data.maxWidth === 'number' ? `${this.data.maxWidth}px` : this.data.maxWidth ?? 'none',
    minHeight: typeof this.data.minHeight === 'number' ? `${this.data.minHeight}px` : this.data.minHeight ?? 'none',
    maxHeight: typeof this.data.maxHeight === 'number' ? `${this.data.maxHeight}px` : this.data.maxHeight ?? 'none',
    margin: typeof this.data.margin === 'number' ? `${this.data.margin}px` : this.data.margin ?? '0',
  };

  readonly headerStyles = this.data.headerStyles;
  readonly footerStyles = this.data.footerStyles;
  readonly bodyStyles = this.data.bodyStyles;

  /**
   * ================================================
   * Extra Data
   * ================================================
   * Extra data to be passed to the callback.
   * This is used to pass data back to the dialog.
   * Note: This is only used if the withDataBindings flag is true.
   */
  extraData: any;

  treeCompactControl = new FormControl<boolean>(false, { nonNullable: true });
  /**
   * ================================================
   * Lifecycle
   * ================================================
   */
  ngOnInit() {
    this.treeCompactControl.setValue(this.data.componentInputs?.['compactView'] ?? false);
  }
  ngOnDestroy() {
    this.events$.complete();
  }
  /**
   * ================================================
   * Actions
   * ================================================
   */
  onActionClick(action: DialogAction) {
    if (this.data.withEventsBindings) {
      this.events$.next({ ref: this.ref, type: 'click', action });
      return;
    }
    if (action.callback) {
      action.callback(this.ref, this.extraData);
    } else if (action.type === DialogActionTypeEnum.DISMISS) {
      this.ref.close();
    }
  }
  /**
   * Callback to be executed when the data changes.
   * This is used to pass data back to the dialog.
   * Note: This is only used if the withDataBindings flag is true.
   * @param data - The data to be passed back to the dialog.
   */
  onDataChange(data: any) {
    if (this.data.withEventsBindings) {
      this.events$.next({ ref: this.ref, type: 'data', data });
      return;
    }
    this.extraData = data;
  }
  /**
   * Close the dialog.
   */
  onClose() {
    if (this.data.withEventsBindings) {
      this.events$.next({ type: 'close', ref: this.ref });
      return;
    }
    this.ref.close();
  }
  changeCompactView() {
    if (this.data.componentInputs) {
      this.data.componentInputs['compactView'] = this.treeCompactControl.value;
    }
    this.compactViewChange$.next(this.treeCompactControl.value);
  }
}
